import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
    LogOut,
    Home,
    BedDouble,
    UtensilsCrossed,
    Image as ImageIcon,
    Star,
    Mail,
    Settings,
    Plus,
    Trash2,
    Save,
    X,
    Calendar as CalIcon,
    Users,
    Sparkles,
    FileText,
    Tag,
    BarChart3,
    HelpCircle,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useLang } from "../lib/LanguageContext";
import { api, imageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "../components/ui/tabs";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "../components/ui/select";
import ImageUploader from "../components/ImageUploader";
import AnalyticsAdmin from "../components/AnalyticsAdmin";
import { useCurrency, CURRENCIES, CURRENCY_SYMBOLS } from "../lib/CurrencyContext";
import { toast } from "sonner";

function AdminCurrencySwitcher() {
    const { currency, setCurrency } = useCurrency();
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                onClick={() => setOpen(!open)}
                data-testid="admin-currency-toggle"
                className="flex items-center gap-1.5 text-xs font-medium tracking-wider uppercase border border-[#0F3A59]/20 text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white px-3 py-1.5 rounded-full transition-colors"
            >
                <span className="text-sm">{currency === "ALL" ? "L" : CURRENCY_SYMBOLS[currency]}</span>
                {currency}
            </button>
            {open && (
                <div className="absolute right-0 top-10 bg-white rounded-2xl shadow-2xl border border-[#E5E0D8] py-2 min-w-[120px] z-50">
                    {CURRENCIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => { setCurrency(c); setOpen(false); }}
                            data-testid={`admin-currency-option-${c}`}
                            className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-[#F4EFE6] ${
                                currency === c ? "text-[#0F3A59] font-semibold" : "text-[#1C1C1C]"
                            }`}
                        >
                            <span className="font-serif w-4 text-center">{c === "ALL" ? "L" : CURRENCY_SYMBOLS[c]}</span>
                            <span>{c}</span>
                            {currency === c && <span className="ml-auto text-[#0F3A59]">✓</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function SectionTitle({ children, action }) {
    return (
        <div className="sticky top-[73px] z-20 -mx-6 px-6 py-4 bg-[#F4EFE6]/95 backdrop-blur-sm flex items-center justify-between mb-6 flex-wrap gap-3">
            <h2 className="font-serif text-2xl text-[#0F3A59]">{children}</h2>
            {action}
        </div>
    );
}

function ImagesEditor({ images = [], onChange }) {
    const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
    const move = (i, dir) => {
        const arr = [...images];
        const j = i + dir;
        if (j < 0 || j >= arr.length) return;
        [arr[i], arr[j]] = [arr[j], arr[i]];
        onChange(arr);
    };
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
                {images.map((img, i) => (
                    <div
                        key={`${img}-${i}`}
                        className="relative w-24 h-24 rounded-lg overflow-hidden border border-[#E5E0D8]"
                    >
                        <img decoding="async" loading="lazy"
                            src={imageUrl(img)}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition flex flex-col items-center justify-center gap-1">
                            <button
                                onClick={() => remove(i)}
                                className="text-white text-xs"
                                data-testid={`remove-image-${i}`}
                            >
                                <Trash2 size={14} />
                            </button>
                            <div className="flex gap-1 text-white text-xs">
                                <button onClick={() => move(i, -1)}>←</button>
                                <button onClick={() => move(i, 1)}>→</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <ImageUploader
                label="Add image"
                onUploaded={(path) => onChange([...images, path])}
                testid="room-image-upload"
            />
        </div>
    );
}

// =========== ROOMS ===========
function RoomsAdmin() {
    const [rooms, setRooms] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/rooms").then((r) => setRooms(r.data));
    useEffect(() => { load(); }, []);

    const blank = {
        slug: "",
        title_al: "",
        title_en: "",
        description_al: "",
        description_en: "",
        price: 50,
        currency: "EUR",
        images: [],
        features_al: [],
        features_en: [],
        order: rooms.length + 1,
        is_featured: true,
    };

    const save = async () => {
        try {
            if (editing.id) {
                await api.put(`/rooms/${editing.id}`, editing);
            } else {
                await api.post("/rooms", editing);
            }
            toast.success("Saved");
            setEditing(null);
            load();
        } catch {
            toast.error("Save failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this room?")) return;
        await api.delete(`/rooms/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div>
            <SectionTitle
                action={
                    <Button
                        onClick={() => setEditing(blank)}
                        data-testid="add-room"
                        className="bg-[#0F3A59] text-white rounded-full"
                    >
                        <Plus size={16} className="mr-2" />
                        Add room
                    </Button>
                }
            >
                Rooms
            </SectionTitle>

            <div className="grid md:grid-cols-2 gap-4">
                {rooms.map((r) => (
                    <div
                        key={r.id}
                        className="bg-white rounded-xl p-5 border border-[#E5E0D8] flex gap-4"
                        data-testid={`admin-room-${r.slug}`}
                    >
                        <img decoding="async" loading="lazy"
                            src={imageUrl(r.images?.[0], imageUrl("seed:room_standard"))}
                            alt=""
                            className="w-24 h-24 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="font-serif text-lg text-[#0F3A59]">
                                {r.title_en}
                            </div>
                            <div className="text-xs text-[#1C1C1C]/60 truncate">
                                {r.description_en}
                            </div>
                            <div className="text-sm font-medium mt-2">€{r.price}/night</div>
                            <div className="flex gap-2 mt-3">
                                <button
                                    onClick={() => setEditing(r)}
                                    className="text-xs px-3 py-1 border border-[#0F3A59]/30 rounded-full text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white"
                                    data-testid={`edit-room-${r.slug}`}
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => remove(r.id)}
                                    className="text-xs px-3 py-1 border border-red-300 rounded-full text-red-600 hover:bg-red-50"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <RoomEditor
                    value={editing}
                    onChange={setEditing}
                    onSave={save}
                    onClose={() => setEditing(null)}
                />
            )}
        </div>
    );
}

function RoomEditor({ value, onChange, onSave, onClose }) {
    const set = (k, v) => onChange({ ...value, [k]: v });
    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="room-editor">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center px-6 sm:px-8 py-5 border-b border-[#E5E0D8] sticky top-0 bg-white rounded-t-2xl">
                    <h3 className="font-serif text-xl sm:text-2xl text-[#0F3A59]">
                        {value.id ? "Edit room" : "Add room"}
                    </h3>
                    <button onClick={onClose} className="p-2 -mr-2 hover:bg-[#F4EFE6] rounded-full">
                        <X size={20} />
                    </button>
                </div>
                <div className="overflow-y-auto px-6 sm:px-8 py-6 flex-1">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Slug</Label>
                        <Input value={value.slug} onChange={(e) => set("slug", e.target.value)} />
                    </div>
                    <div>
                        <Label>Price (€)</Label>
                        <Input
                            type="number"
                            value={value.price}
                            onChange={(e) => set("price", parseFloat(e.target.value) || 0)}
                        />
                    </div>
                    <div>
                        <Label>Title (Albanian)</Label>
                        <Input
                            value={value.title_al}
                            onChange={(e) => set("title_al", e.target.value)}
                            data-testid="room-title-al"
                        />
                    </div>
                    <div>
                        <Label>Title (English)</Label>
                        <Input
                            value={value.title_en}
                            onChange={(e) => set("title_en", e.target.value)}
                            data-testid="room-title-en"
                        />
                    </div>
                </div>
                <div className="mt-4">
                    <Label>Description (Albanian)</Label>
                    <Textarea
                        rows={3}
                        value={value.description_al}
                        onChange={(e) => set("description_al", e.target.value)}
                    />
                </div>
                <div className="mt-4">
                    <Label>Description (English)</Label>
                    <Textarea
                        rows={3}
                        value={value.description_en}
                        onChange={(e) => set("description_en", e.target.value)}
                    />
                </div>
                <div className="mt-4">
                    <Label>Features (Albanian, comma-separated)</Label>
                    <Input
                        value={value.features_al.join(", ")}
                        onChange={(e) =>
                            set(
                                "features_al",
                                e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                            )
                        }
                    />
                </div>
                <div className="mt-4">
                    <Label>Features (English, comma-separated)</Label>
                    <Input
                        value={value.features_en.join(", ")}
                        onChange={(e) =>
                            set(
                                "features_en",
                                e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                            )
                        }
                    />
                </div>
                <div className="mt-4">
                    <Label>Video URL (YouTube/Vimeo, opsionale)</Label>
                    <Input
                        value={value.video_url || ""}
                        onChange={(e) => set("video_url", e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                    />
                </div>
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                        <Label>Çmime sezonale / Seasonal prices</Label>
                        <button
                            type="button"
                            onClick={() => set("seasonal_prices", [
                                ...(value.seasonal_prices || []),
                                { _key: `sp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, label_al: "", label_en: "", start_date: "", end_date: "", price: value.price || 0 },
                            ])}
                            className="text-xs px-3 py-1 border border-[#0F3A59]/30 rounded-full text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white"
                        >
                            + Periudhë
                        </button>
                    </div>
                    <div className="space-y-2">
                        {(value.seasonal_prices || []).map((sp, i) => (
                            <div key={sp._key || `sp-idx-${i}`} className="grid grid-cols-12 gap-2 p-3 bg-[#F4EFE6] rounded-lg items-center">
                                <Input
                                    value={sp.label_al}
                                    onChange={(e) => {
                                        const arr = [...value.seasonal_prices];
                                        arr[i] = { ...sp, label_al: e.target.value };
                                        set("seasonal_prices", arr);
                                    }}
                                    placeholder="P.sh. Sezon Veror"
                                    className="col-span-3 h-9 text-sm"
                                />
                                <Input
                                    value={sp.start_date}
                                    onChange={(e) => {
                                        const arr = [...value.seasonal_prices];
                                        arr[i] = { ...sp, start_date: e.target.value };
                                        set("seasonal_prices", arr);
                                    }}
                                    placeholder="MM-DD"
                                    className="col-span-3 h-9 text-sm"
                                />
                                <Input
                                    value={sp.end_date}
                                    onChange={(e) => {
                                        const arr = [...value.seasonal_prices];
                                        arr[i] = { ...sp, end_date: e.target.value };
                                        set("seasonal_prices", arr);
                                    }}
                                    placeholder="MM-DD"
                                    className="col-span-3 h-9 text-sm"
                                />
                                <Input
                                    type="number"
                                    value={sp.price}
                                    onChange={(e) => {
                                        const arr = [...value.seasonal_prices];
                                        arr[i] = { ...sp, price: parseFloat(e.target.value) || 0 };
                                        set("seasonal_prices", arr);
                                    }}
                                    placeholder="€"
                                    className="col-span-2 h-9 text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        set("seasonal_prices", value.seasonal_prices.filter((_, idx) => idx !== i));
                                    }}
                                    className="col-span-1 text-red-600 hover:bg-red-50 rounded p-1"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                        {(value.seasonal_prices || []).length > 0 && (
                            <p className="text-xs text-[#1C1C1C]/60 font-light">
                                Datat në format <code>MM-DD</code> (p.sh. 07-01 → 08-31 për korrik-gusht). Aplikohet çdo vit.
                            </p>
                        )}
                    </div>
                </div>
                <div className="mt-6">
                    <Label className="block mb-2">Images</Label>
                    <ImagesEditor images={value.images} onChange={(arr) => set("images", arr)} />
                </div>
                </div>
                <div className="border-t border-[#E5E0D8] px-6 sm:px-8 py-4 bg-white flex justify-end gap-2 sticky bottom-0 rounded-b-2xl">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onSave}
                        className="bg-[#0F3A59] hover:bg-[#1C5279] text-white px-6"
                        data-testid="save-room"
                    >
                        <Save size={16} className="mr-2" />
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
}

// =========== FACILITIES ===========
function FacilitiesAdmin() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/facilities").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const blank = {
        slug: "",
        icon: "Star",
        title_al: "",
        title_en: "",
        description_al: "",
        description_en: "",
        image: null,
        order: items.length + 1,
    };

    const save = async () => {
        try {
            if (editing.id) await api.put(`/facilities/${editing.id}`, editing);
            else await api.post("/facilities", editing);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch {
            toast.error("Save failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete?")) return;
        await api.delete(`/facilities/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div>
            <SectionTitle
                action={
                    <Button onClick={() => setEditing(blank)} className="bg-[#0F3A59] text-white rounded-full">
                        <Plus size={16} className="mr-2" />
                        Add facility
                    </Button>
                }
            >
                Facilities
            </SectionTitle>
            <div className="grid md:grid-cols-3 gap-4">
                {items.map((f) => (
                    <div key={f.id} className="bg-white rounded-xl p-4 border border-[#E5E0D8]">
                        <img decoding="async" loading="lazy"
                            src={imageUrl(f.image, imageUrl("seed:facility_restaurant"))}
                            alt=""
                            className="w-full aspect-video object-cover rounded-lg mb-3"
                        />
                        <div className="font-serif text-lg text-[#0F3A59]">{f.title_en}</div>
                        <div className="text-xs text-[#1C1C1C]/60 line-clamp-2 mt-1">
                            {f.description_en}
                        </div>
                        <div className="flex gap-2 mt-3">
                            <button
                                onClick={() => setEditing(f)}
                                className="text-xs px-3 py-1 border border-[#0F3A59]/30 rounded-full text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => remove(f.id)}
                                className="text-xs px-3 py-1 border border-red-300 rounded-full text-red-600 hover:bg-red-50"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                    <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center px-6 sm:px-8 py-5 border-b border-[#E5E0D8] sticky top-0 bg-white rounded-t-2xl">
                            <h3 className="font-serif text-xl sm:text-2xl text-[#0F3A59]">
                                {editing.id ? "Edit facility" : "Add facility"}
                            </h3>
                            <button onClick={() => setEditing(null)} className="p-2 -mr-2 hover:bg-[#F4EFE6] rounded-full">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="overflow-y-auto px-6 sm:px-8 py-6 flex-1">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Slug</Label>
                                <Input
                                    value={editing.slug}
                                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Icon (lucide name)</Label>
                                <Input
                                    value={editing.icon}
                                    onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                                    placeholder="UtensilsCrossed, Wine, ShoppingBasket..."
                                />
                            </div>
                            <div>
                                <Label>Title (AL)</Label>
                                <Input
                                    value={editing.title_al}
                                    onChange={(e) => setEditing({ ...editing, title_al: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Title (EN)</Label>
                                <Input
                                    value={editing.title_en}
                                    onChange={(e) => setEditing({ ...editing, title_en: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="mt-4">
                            <Label>Description (AL)</Label>
                            <Textarea
                                rows={3}
                                value={editing.description_al}
                                onChange={(e) => setEditing({ ...editing, description_al: e.target.value })}
                            />
                        </div>
                        <div className="mt-4">
                            <Label>Description (EN)</Label>
                            <Textarea
                                rows={3}
                                value={editing.description_en}
                                onChange={(e) => setEditing({ ...editing, description_en: e.target.value })}
                            />
                        </div>
                        <div className="mt-4">
                            <Label className="block mb-2">Image</Label>
                            <div className="flex items-center gap-3 flex-wrap">
                                {editing.image && (
                                    <img decoding="async" loading="lazy"
                                        src={imageUrl(editing.image)}
                                        alt=""
                                        className="w-20 h-20 rounded-lg object-cover"
                                    />
                                )}
                                <ImageUploader
                                    onUploaded={(path) => setEditing({ ...editing, image: path })}
                                />
                                {editing.image && (
                                    <button
                                        onClick={() => setEditing({ ...editing, image: null })}
                                        className="text-xs text-red-600"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                        </div>
                        <div className="border-t border-[#E5E0D8] px-6 sm:px-8 py-4 bg-white flex justify-end gap-2 sticky bottom-0 rounded-b-2xl">
                            <Button variant="outline" onClick={() => setEditing(null)}>
                                Cancel
                            </Button>
                            <Button onClick={save} className="bg-[#0F3A59] hover:bg-[#1C5279] text-white px-6">
                                <Save size={16} className="mr-2" />
                                Save
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =========== GALLERY ===========
function GalleryAdmin() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [filter, setFilter] = useState("all");
    const [videoUrl, setVideoUrl] = useState("");
    const [videoPoster, setVideoPoster] = useState(null);

    const load = () => api.get("/gallery").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const filtered = filter === "all" ? items : items.filter((x) => x.category === filter);

    const upload = async (path, category) => {
        setBusy(true);
        try {
            await api.post("/gallery", {
                image: path,
                category,
                caption_al: "",
                caption_en: "",
                order: items.length + 1,
            });
            toast.success("Image added");
            load();
        } finally {
            setBusy(false);
        }
    };

    const addVideo = async () => {
        if (!videoUrl) {
            toast.error("Vendos URL të videos");
            return;
        }
        if (!videoPoster) {
            toast.error("Ngarko një foto si poster për videon");
            return;
        }
        await api.post("/gallery", {
            image: videoPoster,
            video_url: videoUrl,
            category: "videos",
            caption_al: "",
            caption_en: "",
            order: items.length + 1,
        });
        toast.success("Video added");
        setVideoUrl("");
        setVideoPoster(null);
        load();
    };

    const remove = async (id) => {
        if (!confirm("Delete this image?")) return;
        await api.delete(`/gallery/${id}`);
        toast.success("Deleted");
        load();
    };

    const updateCategory = async (item, category) => {
        await api.put(`/gallery/${item.id}`, { ...item, category });
        load();
    };

    return (
        <div>
            <SectionTitle>Gallery</SectionTitle>
            <div className="bg-white rounded-xl p-5 border border-[#E5E0D8] mb-4">
                <div className="text-sm font-medium mb-3">Add image to category</div>
                <div className="flex flex-wrap gap-2">
                    {["rooms", "beach", "restaurant", "hotel"].map((c) => (
                        <CategoryUpload key={c} category={c} onUploaded={(p) => upload(p, c)} />
                    ))}
                </div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-[#E5E0D8] mb-6">
                <div className="text-sm font-medium mb-3">Add video (YouTube / Vimeo / MP4)</div>
                <div className="flex flex-wrap items-center gap-3">
                    <Input
                        placeholder="https://youtube.com/..."
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        className="flex-1 min-w-[200px] h-10"
                        data-testid="video-url-input"
                    />
                    {videoPoster && (
                        <img decoding="async" loading="lazy" src={imageUrl(videoPoster)} alt="" className="w-16 h-16 rounded-lg object-cover" />
                    )}
                    <ImageUploader
                        label={videoPoster ? "Change poster" : "Upload poster image"}
                        onUploaded={setVideoPoster}
                        testid="video-poster"
                    />
                    <Button onClick={addVideo} className="bg-[#0F3A59] text-white rounded-full" data-testid="add-video-btn">
                        Add video
                    </Button>
                </div>
                <p className="text-xs text-[#1C1C1C]/60 font-light mt-2">
                    Posteri është foto që do të shfaqet para se klienti të klikojë "play".
                </p>
            </div>

            <div className="flex gap-2 mb-4 flex-wrap">
                {["all", "rooms", "beach", "restaurant", "hotel", "videos"].map((c) => (
                    <button
                        key={c}
                        onClick={() => setFilter(c)}
                        className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider ${
                            filter === c
                                ? "bg-[#0F3A59] text-white"
                                : "border border-[#0F3A59]/20 text-[#0F3A59]"
                        }`}
                    >
                        {c}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {filtered.map((g) => (
                    <div
                        key={g.id}
                        className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E0D8] group"
                    >
                        <img decoding="async" loading="lazy" src={imageUrl(g.image)} alt="" className="w-full h-full object-cover" />
                        {g.video_url && (
                            <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider">
                                video
                            </div>
                        )}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center gap-2 p-3">
                            <Select
                                value={g.category}
                                onValueChange={(v) => updateCategory(g, v)}
                            >
                                <SelectTrigger className="w-full h-8 text-xs bg-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="rooms">Rooms</SelectItem>
                                    <SelectItem value="beach">Beach</SelectItem>
                                    <SelectItem value="restaurant">Restaurant</SelectItem>
                                    <SelectItem value="hotel">Hotel</SelectItem>
                                    <SelectItem value="videos">Videos</SelectItem>
                                </SelectContent>
                            </Select>
                            <button
                                onClick={() => remove(g.id)}
                                className="text-white text-xs flex items-center gap-1"
                            >
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            {busy && <div className="text-sm mt-4">Uploading...</div>}
        </div>
    );
}

function CategoryUpload({ category, onUploaded }) {
    return (
        <ImageUploader
            label={`+ ${category}`}
            onUploaded={onUploaded}
            testid={`gallery-upload-${category}`}
        />
    );
}

// =========== REVIEWS ===========
function ReviewsAdmin() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/reviews").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const blank = {
        name: "",
        country: "",
        rating: 5,
        text_al: "",
        text_en: "",
        source: "direct",
        source_url: "",
        order: items.length + 1,
    };

    const save = async () => {
        try {
            if (editing.id) await api.put(`/reviews/${editing.id}`, editing);
            else await api.post("/reviews", editing);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch {
            toast.error("Save failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete?")) return;
        await api.delete(`/reviews/${id}`);
        load();
    };

    return (
        <div>
            <SectionTitle
                action={
                    <Button onClick={() => setEditing(blank)} className="bg-[#0F3A59] text-white rounded-full">
                        <Plus size={16} className="mr-2" />
                        Add review
                    </Button>
                }
            >
                Reviews
            </SectionTitle>
            <div className="grid md:grid-cols-2 gap-4">
                {items.map((rv) => (
                    <div key={rv.id} className="bg-white rounded-xl p-5 border border-[#E5E0D8]">
                        <div className="flex justify-between">
                            <div>
                                <div className="font-serif text-lg text-[#0F3A59]">
                                    {rv.name}
                                </div>
                                <div className="text-xs text-[#1C1C1C]/60">{rv.country}</div>
                            </div>
                            <div className="flex">
                                {[...Array(rv.rating)].map((_, i) => (
                                    <Star
                                        key={`star-${i}`}
                                        size={14}
                                        className="fill-[#CBA871] text-[#CBA871]"
                                    />
                                ))}
                            </div>
                        </div>
                        <p className="text-sm text-[#1C1C1C]/70 italic mt-3 line-clamp-2">
                            "{rv.text_en}"
                        </p>
                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() => setEditing(rv)}
                                className="text-xs px-3 py-1 border border-[#0F3A59]/30 rounded-full text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => remove(rv.id)}
                                className="text-xs px-3 py-1 border border-red-300 rounded-full text-red-600 hover:bg-red-50"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                    <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center px-6 sm:px-8 py-5 border-b border-[#E5E0D8] sticky top-0 bg-white rounded-t-2xl">
                            <h3 className="font-serif text-xl sm:text-2xl text-[#0F3A59]">
                                {editing.id ? "Edit review" : "Add review"}
                            </h3>
                            <button onClick={() => setEditing(null)} className="p-2 -mr-2 hover:bg-[#F4EFE6] rounded-full">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="overflow-y-auto px-6 sm:px-8 py-6 flex-1">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Name</Label>
                                <Input
                                    value={editing.name}
                                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Country</Label>
                                <Input
                                    value={editing.country}
                                    onChange={(e) => setEditing({ ...editing, country: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Rating</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="5"
                                    value={editing.rating}
                                    onChange={(e) =>
                                        setEditing({ ...editing, rating: parseInt(e.target.value) || 5 })
                                    }
                                />
                            </div>
                            <div>
                                <Label>Source / Burimi</Label>
                                <Select
                                    value={editing.source || "direct"}
                                    onValueChange={(v) => setEditing({ ...editing, source: v })}
                                >
                                    <SelectTrigger className="h-10">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="direct">Direct (Faqja jonë)</SelectItem>
                                        <SelectItem value="google">Google</SelectItem>
                                        <SelectItem value="booking">Booking.com</SelectItem>
                                        <SelectItem value="tripadvisor">TripAdvisor</SelectItem>
                                        <SelectItem value="airbnb">Airbnb</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Source URL (opsionale — linku te review-i origjinal)</Label>
                                <Input
                                    value={editing.source_url || ""}
                                    onChange={(e) => setEditing({ ...editing, source_url: e.target.value })}
                                    placeholder="https://..."
                                />
                            </div>
                        </div>
                        <div className="mt-4">
                            <Label>Text (AL)</Label>
                            <Textarea
                                rows={3}
                                value={editing.text_al}
                                onChange={(e) => setEditing({ ...editing, text_al: e.target.value })}
                            />
                        </div>
                        <div className="mt-4">
                            <Label>Text (EN)</Label>
                            <Textarea
                                rows={3}
                                value={editing.text_en}
                                onChange={(e) => setEditing({ ...editing, text_en: e.target.value })}
                            />
                        </div>
                        </div>
                        <div className="border-t border-[#E5E0D8] px-6 sm:px-8 py-4 bg-white flex justify-end gap-2 sticky bottom-0 rounded-b-2xl">
                            <Button variant="outline" onClick={() => setEditing(null)}>
                                Cancel
                            </Button>
                            <Button onClick={save} className="bg-[#0F3A59] hover:bg-[#1C5279] text-white px-6">
                                <Save size={16} className="mr-2" />
                                Save
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =========== MESSAGES ===========
function MessagesAdmin() {
    const [items, setItems] = useState([]);

    const load = () => api.get("/contact").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!confirm("Delete this message?")) return;
        await api.delete(`/contact/${id}`);
        load();
    };

    return (
        <div>
            <SectionTitle>Messages ({items.length})</SectionTitle>
            <div className="space-y-3">
                {items.length === 0 && (
                    <div className="text-[#1C1C1C]/60 font-light">No messages yet.</div>
                )}
                {items.map((m) => (
                    <div key={m.id} className="bg-white rounded-xl p-5 border border-[#E5E0D8]">
                        <div className="flex justify-between items-start flex-wrap gap-2">
                            <div>
                                <div className="font-serif text-lg text-[#0F3A59]">{m.name}</div>
                                <div className="text-xs text-[#1C1C1C]/60">
                                    {m.email} {m.phone && `· ${m.phone}`}
                                </div>
                            </div>
                            <div className="text-xs text-[#1C1C1C]/50">
                                {new Date(m.created_at).toLocaleString()}
                            </div>
                        </div>
                        <p className="text-sm text-[#1C1C1C]/80 mt-3">{m.message}</p>
                        <button
                            onClick={() => remove(m.id)}
                            className="text-xs text-red-600 mt-3 hover:underline"
                        >
                            Delete
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

// =========== BOOKINGS ===========
function BookingsAdmin() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");

    const load = () => api.get("/bookings").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const filtered = filter === "all" ? items : items.filter((b) => b.status === filter);

    const updateStatus = async (id, status) => {
        await api.put(`/bookings/${id}`, { status });
        toast.success("Updated");
        load();
    };

    const remove = async (id) => {
        if (!confirm("Delete this booking?")) return;
        await api.delete(`/bookings/${id}`);
        load();
    };

    const statusColors = {
        pending: "bg-amber-100 text-amber-800",
        confirmed: "bg-green-100 text-green-800",
        cancelled: "bg-red-100 text-red-800",
    };

    return (
        <div>
            <SectionTitle>Bookings ({items.length})</SectionTitle>
            <div className="flex gap-2 mb-4 flex-wrap">
                {["all", "pending", "confirmed", "cancelled"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        data-testid={`booking-filter-${s}`}
                        className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider ${
                            filter === s
                                ? "bg-[#0F3A59] text-white"
                                : "border border-[#0F3A59]/20 text-[#0F3A59]"
                        }`}
                    >
                        {s} ({s === "all" ? items.length : items.filter(b => b.status === s).length})
                    </button>
                ))}
            </div>
            <div className="space-y-3">
                {filtered.length === 0 && (
                    <div className="text-[#1C1C1C]/60 font-light py-10 text-center">No bookings yet.</div>
                )}
                {filtered.map((b) => (
                    <div key={b.id} className="bg-white rounded-xl p-5 border border-[#E5E0D8]" data-testid={`booking-${b.id}`}>
                        <div className="flex justify-between items-start flex-wrap gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <h4 className="font-serif text-lg text-[#0F3A59]">{b.name}</h4>
                                    <span className={`text-xs px-2 py-0.5 rounded-full uppercase tracking-wider ${statusColors[b.status] || ""}`}>
                                        {b.status}
                                    </span>
                                </div>
                                <div className="text-xs text-[#1C1C1C]/60 mt-1">
                                    {b.email} {b.phone && `· ${b.phone}`}
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/50">Room</div>
                                        <div className="font-medium">{b.room_slug || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/50">Check-in → out</div>
                                        <div className="font-medium">{b.check_in} → {b.check_out}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/50">Guests · Nights</div>
                                        <div className="font-medium">{b.guests} · {b.nights}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/50">Total</div>
                                        <div className="font-medium text-[#0F3A59]">€{b.total_price}</div>
                                    </div>
                                </div>
                                {b.message && (
                                    <div className="mt-3 text-sm text-[#1C1C1C]/75 bg-[#F4EFE6] rounded-lg p-3">
                                        {b.message}
                                    </div>
                                )}
                            </div>
                            <div className="text-xs text-[#1C1C1C]/50">
                                {new Date(b.created_at).toLocaleString()}
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4 pt-4 border-t border-[#E5E0D8] flex-wrap">
                            {b.status !== "confirmed" && (
                                <button
                                    onClick={() => updateStatus(b.id, "confirmed")}
                                    className="text-xs px-3 py-1 bg-green-600 text-white rounded-full hover:bg-green-700"
                                    data-testid={`confirm-booking-${b.id}`}
                                >
                                    Confirm
                                </button>
                            )}
                            {b.status !== "cancelled" && (
                                <button
                                    onClick={() => updateStatus(b.id, "cancelled")}
                                    className="text-xs px-3 py-1 border border-red-300 text-red-700 rounded-full hover:bg-red-50"
                                >
                                    Cancel
                                </button>
                            )}
                            {b.status !== "pending" && (
                                <button
                                    onClick={() => updateStatus(b.id, "pending")}
                                    className="text-xs px-3 py-1 border border-[#0F3A59]/30 text-[#0F3A59] rounded-full hover:bg-[#0F3A59] hover:text-white"
                                >
                                    Set Pending
                                </button>
                            )}
                            <button
                                onClick={() => remove(b.id)}
                                className="text-xs px-3 py-1 text-red-600 hover:underline ml-auto"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// =========== NEWSLETTER ===========
function NewsletterAdmin() {
    const [items, setItems] = useState([]);

    const load = () => api.get("/newsletter").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!confirm("Remove this subscriber?")) return;
        await api.delete(`/newsletter/${id}`);
        load();
    };

    const exportCSV = () => {
        const csv = "email,name,created_at\n" + items.map(s =>
            `${s.email},"${(s.name || "").replace(/"/g, '""')}",${s.created_at}`
        ).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `newsletter-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div>
            <SectionTitle
                action={
                    items.length > 0 && (
                        <Button onClick={exportCSV} className="bg-[#0F3A59] text-white rounded-full" data-testid="export-newsletter">
                            Export CSV
                        </Button>
                    )
                }
            >
                Newsletter Subscribers ({items.length})
            </SectionTitle>
            <div className="bg-white rounded-xl border border-[#E5E0D8] overflow-hidden">
                {items.length === 0 ? (
                    <div className="p-10 text-center text-[#1C1C1C]/60 font-light">
                        No subscribers yet.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4EFE6] text-left text-xs uppercase tracking-wider text-[#1C1C1C]/70">
                            <tr>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3 hidden sm:table-cell">Name</th>
                                <th className="px-4 py-3 hidden sm:table-cell">Date</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((s) => (
                                <tr key={s.id} className="border-t border-[#E5E0D8]">
                                    <td className="px-4 py-3 font-medium">{s.email}</td>
                                    <td className="px-4 py-3 hidden sm:table-cell">{s.name || "—"}</td>
                                    <td className="px-4 py-3 hidden sm:table-cell text-[#1C1C1C]/60">
                                        {new Date(s.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => remove(s.id)} className="text-xs text-red-600 hover:underline">
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

// =========== ADDONS (Shërbime Ekstra) ===========
function AddonsAdmin() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/addons").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const blank = {
        title_al: "", title_en: "", title_it: "", title_de: "",
        description_al: "", description_en: "", description_it: "", description_de: "",
        price: 0,
        price_type: "flat",
        icon: "Plus",
        is_active: true,
        order: items.length + 1,
    };

    const save = async () => {
        try {
            const payload = { ...editing, price: parseFloat(editing.price) || 0, order: parseInt(editing.order) || 0 };
            if (editing.id) await api.put(`/admin/addons/${editing.id}`, payload);
            else await api.post("/admin/addons", payload);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch {
            toast.error("Save failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this add-on?")) return;
        await api.delete(`/admin/addons/${id}`);
        load();
    };

    const PRICE_TYPES = [
        { value: "flat", label: "Fiks (një herë)" },
        { value: "per_night", label: "Për natë" },
        { value: "per_person", label: "Për person" },
        { value: "per_person_night", label: "Për person për natë" },
    ];

    return (
        <div>
            <SectionTitle
                action={
                    <Button onClick={() => setEditing(blank)} data-testid="addon-add-btn" className="bg-[#0F3A59] text-white rounded-full">
                        <Plus size={16} className="mr-2" />
                        Add-on i ri
                    </Button>
                }
            >
                Shërbime Ekstra
            </SectionTitle>

            <div className="grid md:grid-cols-2 gap-4">
                {items.map((a) => (
                    <div key={a.id} data-testid={`addon-row-${a.id}`} className={`bg-white rounded-xl p-5 border ${a.is_active ? "border-[#E5E0D8]" : "border-red-200 opacity-70"}`}>
                        <div className="flex justify-between items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="font-serif text-lg text-[#0F3A59] truncate">{a.title_al || a.title_en}</div>
                                <div className="text-xs text-[#1C1C1C]/60 truncate">{a.title_en}</div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="font-serif text-xl text-[#0F3A59]">€{a.price}</div>
                                <div className="text-[10px] uppercase tracking-wider text-[#1C1C1C]/55">{a.price_type.replace(/_/g, " ")}</div>
                            </div>
                        </div>
                        <p className="text-sm text-[#1C1C1C]/65 mt-2 line-clamp-2 font-light">{a.description_al || a.description_en}</p>
                        <div className="flex gap-2 mt-4 items-center">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                {a.is_active ? "Aktiv" : "Jo aktiv"}
                            </span>
                            <span className="text-[10px] text-[#1C1C1C]/55 ml-auto">Icon: {a.icon}</span>
                            <button onClick={() => setEditing(a)} data-testid={`addon-edit-${a.id}`} className="text-xs px-3 py-1 border border-[#0F3A59]/30 rounded-full text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white">Modifiko</button>
                            <button onClick={() => remove(a.id)} className="text-xs px-3 py-1 border border-red-300 rounded-full text-red-600 hover:bg-red-50">
                                <Trash2 size={12} />
                            </button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="text-center py-12 text-[#1C1C1C]/55 col-span-2 font-light">
                        Asnjë shërbim ekstra. Shtoni të parën!
                    </div>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-[#E5E0D8] px-6 py-4 flex justify-between items-center z-10">
                            <h3 className="font-serif text-xl text-[#0F3A59]">
                                {editing.id ? "Modifiko Shërbim" : "Shtoni Shërbim Ekstra"}
                            </h3>
                            <button onClick={() => setEditing(null)} className="text-[#1C1C1C]/60"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Titulli (AL)</Label>
                                    <Input value={editing.title_al} onChange={(e) => setEditing({ ...editing, title_al: e.target.value })} data-testid="addon-title-al" />
                                </div>
                                <div>
                                    <Label>Titulli (EN)</Label>
                                    <Input value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} data-testid="addon-title-en" />
                                </div>
                                <div>
                                    <Label>Titulli (IT)</Label>
                                    <Input value={editing.title_it} onChange={(e) => setEditing({ ...editing, title_it: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Titulli (DE)</Label>
                                    <Input value={editing.title_de} onChange={(e) => setEditing({ ...editing, title_de: e.target.value })} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Përshkrim (AL)</Label>
                                    <Textarea rows={3} value={editing.description_al} onChange={(e) => setEditing({ ...editing, description_al: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Përshkrim (EN)</Label>
                                    <Textarea rows={3} value={editing.description_en} onChange={(e) => setEditing({ ...editing, description_en: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Përshkrim (IT)</Label>
                                    <Textarea rows={3} value={editing.description_it} onChange={(e) => setEditing({ ...editing, description_it: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Përshkrim (DE)</Label>
                                    <Textarea rows={3} value={editing.description_de} onChange={(e) => setEditing({ ...editing, description_de: e.target.value })} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Çmimi (€)</Label>
                                    <Input type="number" step="0.5" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="addon-price" />
                                </div>
                                <div>
                                    <Label>Lloji i çmimit</Label>
                                    <Select value={editing.price_type} onValueChange={(v) => setEditing({ ...editing, price_type: v })}>
                                        <SelectTrigger data-testid="addon-price-type"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {PRICE_TYPES.map((p) => (
                                                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Ikona (Lucide)</Label>
                                    <Input value={editing.icon} placeholder="P.sh. Coffee, Car, Wine, Umbrella" onChange={(e) => setEditing({ ...editing, icon: e.target.value })} />
                                    <p className="text-[11px] text-[#1C1C1C]/55 mt-1">Shfletoni te lucide.dev/icons</p>
                                </div>
                                <div>
                                    <Label>Renditja</Label>
                                    <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={editing.is_active}
                                    onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                                    className="w-4 h-4"
                                />
                                <span className="text-sm">Aktiv (i shfaqet klientëve)</span>
                            </label>
                        </div>
                        <div className="sticky bottom-0 bg-white border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-[#E5E0D8] rounded-full text-sm">Anulo</button>
                            <Button onClick={save} data-testid="addon-save-btn" className="bg-[#0F3A59] text-white rounded-full">
                                <Save size={14} className="mr-2" />
                                Ruaj
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =========== BLOG ===========
function BlogAdmin() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [activeLang, setActiveLang] = useState("al");
    const [aiOpen, setAiOpen] = useState(false);
    const [aiTopic, setAiTopic] = useState("");
    const [aiCategory, setAiCategory] = useState("general");
    const [aiBusy, setAiBusy] = useState(false);

    const load = () => api.get("/admin/blog").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const blank = {
        slug: "",
        cover_image: "",
        category: "general",
        status: "draft",
        title_al: "", title_en: "", title_it: "", title_de: "",
        excerpt_al: "", excerpt_en: "", excerpt_it: "", excerpt_de: "",
        content_al: "", content_en: "", content_it: "", content_de: "",
        published_at: "",
    };

    const slugify = (s) => s.toLowerCase()
        .replace(/ë/g, "e").replace(/ç/g, "c").replace(/ü/g, "u").replace(/ö/g, "o").replace(/ä/g, "a")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    const save = async () => {
        try {
            const payload = { ...editing };
            if (!payload.slug && payload.title_al) payload.slug = slugify(payload.title_al);
            if (!payload.slug) {
                toast.error("Slug ose titulli AL është i detyrueshëm");
                return;
            }
            if (editing.id) await api.put(`/admin/blog/${editing.id}`, payload);
            else await api.post("/admin/blog", payload);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this post?")) return;
        await api.delete(`/admin/blog/${id}`);
        load();
    };

    const generateAI = async () => {
        if (!aiTopic.trim()) {
            toast.error("Vendos një temë");
            return;
        }
        setAiBusy(true);
        try {
            const res = await api.post("/admin/blog/generate", {
                topic: aiTopic.trim(),
                category: aiCategory,
                primary_lang: "al",
            });
            // Open the editor pre-filled with AI content; admin reviews then saves
            setEditing({
                ...blank,
                ...res.data,
                cover_image: "",
                published_at: "",
            });
            setAiOpen(false);
            setAiTopic("");
            toast.success("AI gjeneroi postimin! Rishikoni dhe ruani.");
        } catch (e) {
            const detail = e?.response?.data?.detail || "AI generation failed";
            toast.error(detail, { duration: 8000 });
        } finally {
            setAiBusy(false);
        }
    };

    const CATS = [
        { value: "beaches", label: "Plazhe" },
        { value: "restaurant", label: "Restorant" },
        { value: "attractions", label: "Atraksione" },
        { value: "tips", label: "Këshilla" },
        { value: "general", label: "Tjera" },
    ];
    const LANGS = [
        { code: "al", label: "🇦🇱 AL" },
        { code: "en", label: "🇬🇧 EN" },
        { code: "it", label: "🇮🇹 IT" },
        { code: "de", label: "🇩🇪 DE" },
    ];

    return (
        <div>
            <SectionTitle
                action={
                    <div className="flex gap-2">
                        <Button
                            onClick={() => setAiOpen(true)}
                            data-testid="blog-ai-btn"
                            className="bg-gradient-to-br from-[#CBA871] to-[#A88758] text-white rounded-full"
                        >
                            <Sparkles size={16} className="mr-2" />
                            Gjenero me AI
                        </Button>
                        <Button onClick={() => setEditing(blank)} data-testid="blog-add-btn" className="bg-[#0F3A59] text-white rounded-full">
                            <Plus size={16} className="mr-2" />
                            Postim i ri
                        </Button>
                    </div>
                }
            >
                Blog & Udhëzues
            </SectionTitle>

            <div className="grid md:grid-cols-2 gap-4">
                {items.map((p) => (
                    <div key={p.id} data-testid={`blog-row-${p.id}`} className="bg-white rounded-xl overflow-hidden border border-[#E5E0D8] flex">
                        {p.cover_image && (
                            <div className="w-32 shrink-0 bg-[#F4EFE6]">
                                <img decoding="async" loading="lazy" src={imageUrl(p.cover_image)} alt="" className="w-full h-full object-cover" />
                            </div>
                        )}
                        <div className="flex-1 p-4 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${p.status === "published" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                                    {p.status}
                                </span>
                                <span className="text-[10px] text-[#1C1C1C]/55">{p.category}</span>
                            </div>
                            <h3 className="font-serif text-base text-[#0F3A59] line-clamp-2 leading-snug">
                                {p.title_al || p.title_en}
                            </h3>
                            <div className="text-[11px] text-[#1C1C1C]/55 mt-1 truncate">/blog/{p.slug}</div>
                            <div className="flex gap-2 mt-3">
                                <button onClick={() => setEditing(p)} data-testid={`blog-edit-${p.id}`} className="text-xs px-3 py-1 border border-[#0F3A59]/30 rounded-full text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white">Modifiko</button>
                                <button onClick={() => remove(p.id)} className="text-xs px-3 py-1 border border-red-300 rounded-full text-red-600 hover:bg-red-50">
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="text-center py-12 text-[#1C1C1C]/55 col-span-2 font-light">
                        Asnjë postim. Krijoni të parin!
                    </div>
                )}
            </div>

            {aiOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-lg">
                        <div className="border-b border-[#E5E0D8] px-6 py-4 flex justify-between items-center">
                            <h3 className="font-serif text-xl text-[#0F3A59] flex items-center gap-2">
                                <Sparkles size={18} className="text-[#CBA871]" />
                                Gjenero Postim me AI
                            </h3>
                            <button onClick={() => setAiOpen(false)} className="text-[#1C1C1C]/60"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-[#1C1C1C]/70 font-light">
                                AI do shkruajë një postim të plotë në 4 gjuhë (AL/EN/IT/DE) bazuar në temën që ju jepni. Pas gjenerimit, do mund ta rishikoni dhe modifikoni para se ta ruani.
                            </p>
                            <div>
                                <Label>Tema e postimit *</Label>
                                <Textarea
                                    rows={3}
                                    value={aiTopic}
                                    onChange={(e) => setAiTopic(e.target.value)}
                                    placeholder="P.sh.: Plazhi privat i Kompleksit Dea — pse është më i miri në Radhimë. OR: Si të organizoni dasmën e ëndrrave në Rivierën Shqiptare"
                                    data-testid="ai-topic-input"
                                />
                                <p className="text-[11px] text-[#1C1C1C]/55 mt-1">Sa më specifike tema, aq më cilësore postimi.</p>
                            </div>
                            <div>
                                <Label>Kategoria</Label>
                                <Select value={aiCategory} onValueChange={setAiCategory}>
                                    <SelectTrigger data-testid="ai-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="beaches">Plazhe</SelectItem>
                                        <SelectItem value="restaurant">Restorant</SelectItem>
                                        <SelectItem value="attractions">Atraksione</SelectItem>
                                        <SelectItem value="tips">Këshilla</SelectItem>
                                        <SelectItem value="general">Tjera</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="bg-[#F4EFE6]/50 border border-[#CBA871]/30 rounded-xl p-3 text-xs text-[#1C1C1C]/75 font-light">
                                ⏱️ Gjenerimi zgjat ~30-60 sekonda. Universal Key Budget shfrytëzohet. Statusi i postimit do jetë "draft" — duhet ta publikoni manualisht.
                            </div>
                        </div>
                        <div className="border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setAiOpen(false)} disabled={aiBusy} className="px-5 py-2 border border-[#E5E0D8] rounded-full text-sm disabled:opacity-50">Anulo</button>
                            <Button onClick={generateAI} disabled={aiBusy || !aiTopic.trim()} data-testid="ai-generate-btn" className="bg-gradient-to-br from-[#CBA871] to-[#A88758] text-white rounded-full">
                                <Sparkles size={14} className="mr-2" />
                                {aiBusy ? "Po gjenerohet..." : "Gjenero"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-[#E5E0D8] px-6 py-4 flex justify-between items-center z-10">
                            <h3 className="font-serif text-xl text-[#0F3A59]">
                                {editing.id ? "Modifiko Postimin" : "Postim i ri"}
                            </h3>
                            <button onClick={() => setEditing(null)} className="text-[#1C1C1C]/60"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Slug (URL)</Label>
                                    <Input
                                        value={editing.slug}
                                        onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
                                        placeholder="p.sh. 10-plazhet-me-te-mira"
                                        data-testid="blog-slug"
                                    />
                                    <p className="text-[11px] text-[#1C1C1C]/55 mt-1">Lihet bosh → krijohet nga titulli AL</p>
                                </div>
                                <div>
                                    <Label>Kategoria</Label>
                                    <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {CATS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Statusi</Label>
                                    <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                                        <SelectTrigger data-testid="blog-status"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="draft">Draft (i fshehur)</SelectItem>
                                            <SelectItem value="published">Publikuar</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Data e publikimit (opsionale)</Label>
                                    <Input
                                        type="datetime-local"
                                        value={editing.published_at ? editing.published_at.slice(0, 16) : ""}
                                        onChange={(e) => setEditing({ ...editing, published_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                                    />
                                </div>
                            </div>

                            <div>
                                <Label>Foto kryesore (cover)</Label>
                                <ImagesEditor
                                    images={editing.cover_image ? [editing.cover_image] : []}
                                    onChange={(arr) => setEditing({ ...editing, cover_image: arr[0] || "" })}
                                />
                            </div>

                            {/* Language Tabs */}
                            <div>
                                <div className="flex gap-1 border-b border-[#E5E0D8] mb-3">
                                    {LANGS.map((l) => (
                                        <button
                                            key={l.code}
                                            type="button"
                                            onClick={() => setActiveLang(l.code)}
                                            data-testid={`blog-lang-tab-${l.code}`}
                                            className={`px-4 py-2 text-sm border-b-2 transition-colors ${
                                                activeLang === l.code
                                                    ? "border-[#0F3A59] text-[#0F3A59] font-medium"
                                                    : "border-transparent text-[#1C1C1C]/55 hover:text-[#0F3A59]"
                                            }`}
                                        >
                                            {l.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <Label>Titulli ({activeLang.toUpperCase()})</Label>
                                        <Input
                                            value={editing[`title_${activeLang}`] || ""}
                                            onChange={(e) => setEditing({ ...editing, [`title_${activeLang}`]: e.target.value })}
                                            data-testid={`blog-title-${activeLang}`}
                                        />
                                    </div>
                                    <div>
                                        <Label>Përshkrim i shkurtër / Excerpt ({activeLang.toUpperCase()})</Label>
                                        <Textarea
                                            rows={2}
                                            value={editing[`excerpt_${activeLang}`] || ""}
                                            onChange={(e) => setEditing({ ...editing, [`excerpt_${activeLang}`]: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label>Përmbajtja (Markdown) ({activeLang.toUpperCase()})</Label>
                                        <Textarea
                                            rows={14}
                                            value={editing[`content_${activeLang}`] || ""}
                                            onChange={(e) => setEditing({ ...editing, [`content_${activeLang}`]: e.target.value })}
                                            data-testid={`blog-content-${activeLang}`}
                                            className="font-mono text-sm"
                                            placeholder="## Titull i seksionit&#10;&#10;Paragrafi yt këtu...&#10;&#10;**Bold** ose *italic*. Lista:&#10;- Pikë e parë&#10;- Pikë e dytë"
                                        />
                                        <p className="text-[11px] text-[#1C1C1C]/55 mt-1">
                                            Markdown: <code>## Titull</code>, <code>**bold**</code>, <code>*italic*</code>, <code>- listë</code>, <code>---</code> për ndarje
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="sticky bottom-0 bg-white border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-[#E5E0D8] rounded-full text-sm">Anulo</button>
                            <Button onClick={save} data-testid="blog-save-btn" className="bg-[#0F3A59] text-white rounded-full">
                                <Save size={14} className="mr-2" />
                                Ruaj
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =========== PROMO CODES ===========
function PromoAdmin() {
    const [items, setItems] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/promo").then((r) => setItems(r.data));
    useEffect(() => {
        load();
        api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    }, []);

    const blank = {
        code: "",
        discount_type: "percentage",
        discount_value: 10,
        start_date: "",
        end_date: "",
        max_uses: 0,
        is_active: true,
        room_slugs: [],
        min_nights: 0,
        description_al: "",
        description_en: "",
        description_it: "",
        description_de: "",
    };

    const save = async () => {
        try {
            const payload = {
                ...editing,
                code: (editing.code || "").toUpperCase().trim(),
                discount_value: parseFloat(editing.discount_value) || 0,
                max_uses: parseInt(editing.max_uses) || 0,
                min_nights: parseInt(editing.min_nights) || 0,
            };
            if (!payload.code) {
                toast.error("Code is required");
                return;
            }
            if (editing.id) await api.put(`/admin/promo/${editing.id}`, payload);
            else await api.post("/admin/promo", payload);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this promo code?")) return;
        await api.delete(`/admin/promo/${id}`);
        load();
    };

    const toggleRoom = (slug) => {
        const list = editing.room_slugs || [];
        const next = list.includes(slug) ? list.filter((x) => x !== slug) : [...list, slug];
        setEditing({ ...editing, room_slugs: next });
    };

    const formatDiscount = (p) => p.discount_type === "percentage" ? `${p.discount_value}%` : `€${p.discount_value}`;
    const isExpired = (p) => p.end_date && new Date(p.end_date) < new Date(new Date().toDateString());

    return (
        <div>
            <SectionTitle
                action={
                    <Button onClick={() => setEditing(blank)} data-testid="promo-add-btn" className="bg-[#0F3A59] text-white rounded-full">
                        <Plus size={16} className="mr-2" />
                        Promo Code i ri
                    </Button>
                }
            >
                Promo Codes
            </SectionTitle>

            <div className="grid md:grid-cols-2 gap-4">
                {items.map((p) => {
                    const exhausted = p.max_uses > 0 && p.current_uses >= p.max_uses;
                    const expired = isExpired(p);
                    return (
                        <div
                            key={p.id}
                            data-testid={`promo-row-${p.id}`}
                            className={`bg-white rounded-xl p-5 border ${p.is_active && !expired && !exhausted ? "border-[#E5E0D8]" : "border-red-200 opacity-75"}`}
                        >
                            <div className="flex justify-between items-start gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0F3A59] text-white text-xs font-mono tracking-wider">
                                        {p.code}
                                    </div>
                                    {(p.description_al || p.description_en) && (
                                        <p className="text-sm text-[#1C1C1C]/65 mt-2 line-clamp-2 font-light">
                                            {p.description_al || p.description_en}
                                        </p>
                                    )}
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-serif text-2xl text-[#CBA871]">−{formatDiscount(p)}</div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-[#1C1C1C]/65">
                                {p.start_date && (
                                    <div><strong>Nga:</strong> {p.start_date}</div>
                                )}
                                {p.end_date && (
                                    <div><strong>Deri:</strong> {p.end_date}</div>
                                )}
                                <div>
                                    <strong>Përdorime:</strong> {p.current_uses}{p.max_uses > 0 ? `/${p.max_uses}` : ` / ∞`}
                                </div>
                                {p.min_nights > 0 && (
                                    <div><strong>Min net:</strong> {p.min_nights}</div>
                                )}
                            </div>

                            <div className="flex items-center gap-2 mt-3 flex-wrap">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                                    {p.is_active ? "Aktiv" : "Jo aktiv"}
                                </span>
                                {expired && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">Skaduar</span>
                                )}
                                {exhausted && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Limit i mbushur</span>
                                )}
                                {p.room_slugs && p.room_slugs.length > 0 && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                        {p.room_slugs.length} dhoma
                                    </span>
                                )}
                                <button
                                    onClick={() => setEditing(p)}
                                    data-testid={`promo-edit-${p.id}`}
                                    className="text-xs px-3 py-1 border border-[#0F3A59]/30 rounded-full text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white ml-auto"
                                >
                                    Modifiko
                                </button>
                                <button
                                    onClick={() => remove(p.id)}
                                    className="text-xs px-3 py-1 border border-red-300 rounded-full text-red-600 hover:bg-red-50"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    );
                })}
                {items.length === 0 && (
                    <div className="text-center py-12 text-[#1C1C1C]/55 col-span-2 font-light">
                        Asnjë promo code. Krijoni të parin për sezonin!
                    </div>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-[#E5E0D8] px-6 py-4 flex justify-between items-center z-10">
                            <h3 className="font-serif text-xl text-[#0F3A59]">
                                {editing.id ? "Modifiko Promo Code" : "Promo Code i ri"}
                            </h3>
                            <button onClick={() => setEditing(null)} className="text-[#1C1C1C]/60"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <Label>Emri i kodit *</Label>
                                <Input
                                    value={editing.code}
                                    onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                                    placeholder="P.sh. SUMMER2026"
                                    data-testid="promo-code-input"
                                    className="uppercase tracking-wider font-mono"
                                />
                                <p className="text-[11px] text-[#1C1C1C]/55 mt-1">Vetëm shkronja të mëdha, numra (pa hapësira)</p>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Lloji i uljes</Label>
                                    <Select
                                        value={editing.discount_type}
                                        onValueChange={(v) => setEditing({ ...editing, discount_type: v })}
                                    >
                                        <SelectTrigger data-testid="promo-type"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="percentage">% Përqindje</SelectItem>
                                            <SelectItem value="fixed">€ Shumë fikse</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Vlera {editing.discount_type === "percentage" ? "(%)" : "(€)"} *</Label>
                                    <Input
                                        type="number"
                                        step="0.5"
                                        value={editing.discount_value}
                                        onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })}
                                        data-testid="promo-value"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Data e fillimit</Label>
                                    <Input
                                        type="date"
                                        value={editing.start_date}
                                        onChange={(e) => setEditing({ ...editing, start_date: e.target.value })}
                                        data-testid="promo-start"
                                    />
                                </div>
                                <div>
                                    <Label>Data e mbarimit</Label>
                                    <Input
                                        type="date"
                                        value={editing.end_date}
                                        onChange={(e) => setEditing({ ...editing, end_date: e.target.value })}
                                        data-testid="promo-end"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Maks. përdorime (0 = pakufi)</Label>
                                    <Input
                                        type="number"
                                        value={editing.max_uses}
                                        onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })}
                                        data-testid="promo-max-uses"
                                    />
                                </div>
                                <div>
                                    <Label>Min. netë (0 = pa minimum)</Label>
                                    <Input
                                        type="number"
                                        value={editing.min_nights}
                                        onChange={(e) => setEditing({ ...editing, min_nights: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <Label>Dhomat e lejuara (asnjë i selektuar = të gjitha dhomat)</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {rooms.map((r) => {
                                        const checked = (editing.room_slugs || []).includes(r.slug);
                                        return (
                                            <button
                                                key={r.id}
                                                type="button"
                                                onClick={() => toggleRoom(r.slug)}
                                                data-testid={`promo-room-${r.slug}`}
                                                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                                                    checked
                                                        ? "bg-[#0F3A59] text-white border-[#0F3A59]"
                                                        : "bg-white text-[#0F3A59] border-[#E5E0D8] hover:border-[#0F3A59]"
                                                }`}
                                            >
                                                {r.title_al || r.title_en}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Përshkrim (AL) — opsionale</Label>
                                    <Textarea
                                        rows={2}
                                        value={editing.description_al}
                                        onChange={(e) => setEditing({ ...editing, description_al: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label>Përshkrim (EN) — opsionale</Label>
                                    <Textarea
                                        rows={2}
                                        value={editing.description_en}
                                        onChange={(e) => setEditing({ ...editing, description_en: e.target.value })}
                                    />
                                </div>
                            </div>

                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={editing.is_active}
                                    onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                                    data-testid="promo-active"
                                    className="w-4 h-4"
                                />
                                <span className="text-sm">Aktiv (mund të përdoret nga klientët)</span>
                            </label>
                        </div>
                        <div className="sticky bottom-0 bg-white border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-[#E5E0D8] rounded-full text-sm">Anulo</button>
                            <Button onClick={save} data-testid="promo-save-btn" className="bg-[#0F3A59] text-white rounded-full">
                                <Save size={14} className="mr-2" />
                                Ruaj
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =========== FAQ ADMIN ===========
function FaqAdmin() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/faqs").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const blank = {
        question_al: "", question_en: "", question_it: "", question_de: "",
        answer_al: "", answer_en: "", answer_it: "", answer_de: "",
        is_active: true,
        order: items.length + 1,
    };

    const save = async () => {
        if (!editing.question_al?.trim() && !editing.question_en?.trim()) {
            toast.error("Shto të paktën një pyetje (AL ose EN)");
            return;
        }
        try {
            const payload = { ...editing, order: parseInt(editing.order) || 0 };
            if (editing.id) await api.put(`/admin/faqs/${editing.id}`, payload);
            else await api.post("/admin/faqs", payload);
            toast.success("Ruajtur");
            setEditing(null);
            load();
        } catch {
            toast.error("Ruajtja dështoi");
        }
    };

    const remove = async (id) => {
        if (!confirm("Fshi këtë FAQ?")) return;
        await api.delete(`/admin/faqs/${id}`);
        load();
    };

    return (
        <div>
            <SectionTitle
                action={
                    <Button onClick={() => setEditing(blank)} data-testid="faq-add-btn" className="bg-[#0F3A59] text-white rounded-full">
                        <Plus size={16} className="mr-2" />
                        FAQ e re
                    </Button>
                }
            >
                Pyetjet e Shpeshta (FAQ)
            </SectionTitle>

            <div className="space-y-3">
                {items.map((f) => (
                    <div
                        key={f.id}
                        data-testid={`faq-admin-row-${f.id}`}
                        className={`bg-white rounded-xl p-5 border ${f.is_active ? "border-[#E5E0D8]" : "border-red-200 opacity-70"}`}
                    >
                        <div className="flex justify-between items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F4EFE6] text-[#0F3A59]">#{f.order}</span>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${f.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                        {f.is_active ? "Aktiv" : "Jo aktiv"}
                                    </span>
                                </div>
                                <div className="font-serif text-lg text-[#0F3A59]">{f.question_al || f.question_en}</div>
                                <div className="text-xs text-[#1C1C1C]/60 mt-0.5">{f.question_en}</div>
                                <p className="text-sm text-[#1C1C1C]/70 mt-2 line-clamp-2 font-light">{f.answer_al || f.answer_en}</p>
                            </div>
                            <div className="flex flex-col gap-2 shrink-0">
                                <button
                                    onClick={() => setEditing(f)}
                                    data-testid={`faq-edit-${f.id}`}
                                    className="text-xs px-3 py-1 border border-[#0F3A59]/30 rounded-full text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white"
                                >
                                    Modifiko
                                </button>
                                <button
                                    onClick={() => remove(f.id)}
                                    data-testid={`faq-delete-${f.id}`}
                                    className="text-xs px-3 py-1 border border-red-300 rounded-full text-red-600 hover:bg-red-50 flex items-center justify-center"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="text-center py-12 text-[#1C1C1C]/55 font-light">
                        Asnjë FAQ. Shtoni të parën!
                    </div>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-[#E5E0D8] px-6 py-4 flex justify-between items-center z-10">
                            <h3 className="font-serif text-xl text-[#0F3A59]">
                                {editing.id ? "Modifiko FAQ" : "Shtoni FAQ"}
                            </h3>
                            <button onClick={() => setEditing(null)} className="text-[#1C1C1C]/60"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div>
                                <h4 className="text-xs uppercase tracking-widest text-[#0F3A59]/70 mb-2 font-semibold">Pyetja</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <Label>Pyetja (AL)</Label>
                                        <Input value={editing.question_al} onChange={(e) => setEditing({ ...editing, question_al: e.target.value })} data-testid="faq-question-al" />
                                    </div>
                                    <div>
                                        <Label>Question (EN)</Label>
                                        <Input value={editing.question_en} onChange={(e) => setEditing({ ...editing, question_en: e.target.value })} data-testid="faq-question-en" />
                                    </div>
                                    <div>
                                        <Label>Domanda (IT)</Label>
                                        <Input value={editing.question_it} onChange={(e) => setEditing({ ...editing, question_it: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label>Frage (DE)</Label>
                                        <Input value={editing.question_de} onChange={(e) => setEditing({ ...editing, question_de: e.target.value })} />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-xs uppercase tracking-widest text-[#0F3A59]/70 mb-2 font-semibold">Përgjigjja</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <Label>Përgjigjja (AL)</Label>
                                        <Textarea rows={4} value={editing.answer_al} onChange={(e) => setEditing({ ...editing, answer_al: e.target.value })} data-testid="faq-answer-al" />
                                    </div>
                                    <div>
                                        <Label>Answer (EN)</Label>
                                        <Textarea rows={4} value={editing.answer_en} onChange={(e) => setEditing({ ...editing, answer_en: e.target.value })} data-testid="faq-answer-en" />
                                    </div>
                                    <div>
                                        <Label>Risposta (IT)</Label>
                                        <Textarea rows={4} value={editing.answer_it} onChange={(e) => setEditing({ ...editing, answer_it: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label>Antwort (DE)</Label>
                                        <Textarea rows={4} value={editing.answer_de} onChange={(e) => setEditing({ ...editing, answer_de: e.target.value })} />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Renditja</Label>
                                    <Input
                                        type="number"
                                        value={editing.order}
                                        onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                                        data-testid="faq-order"
                                    />
                                    <p className="text-[11px] text-[#1C1C1C]/55 mt-1">Numër më i vogël = shfaqet më lart.</p>
                                </div>
                                <div className="flex items-end pb-1">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editing.is_active}
                                            onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                                            className="w-4 h-4"
                                            data-testid="faq-active"
                                        />
                                        <span className="text-sm">Aktiv (i shfaqet klientëve)</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="sticky bottom-0 bg-white border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-[#E5E0D8] rounded-full text-sm">Anulo</button>
                            <Button onClick={save} data-testid="faq-save-btn" className="bg-[#0F3A59] text-white rounded-full">
                                <Save size={14} className="mr-2" />
                                Ruaj
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =========== PRESS / FEATURED IN ===========
function PressAdmin() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/admin/press").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const blank = {
        name: "",
        logo: "",
        url: "",
        quote_al: "",
        quote_en: "",
        quote_it: "",
        quote_de: "",
        order: items.length + 1,
        is_active: true,
    };

    const save = async () => {
        try {
            const payload = { ...editing, order: parseInt(editing.order) || 0 };
            if (!payload.name) {
                toast.error("Emri është i detyrueshëm");
                return;
            }
            if (editing.id) await api.put(`/admin/press/${editing.id}`, payload);
            else await api.post("/admin/press", payload);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this press item?")) return;
        await api.delete(`/admin/press/${id}`);
        load();
    };

    return (
        <div>
            <SectionTitle
                action={
                    <Button onClick={() => setEditing(blank)} data-testid="press-add-btn" className="bg-[#0F3A59] text-white rounded-full">
                        <Plus size={16} className="mr-2" />
                        Shto Mention
                    </Button>
                }
            >
                Featured In / Press
            </SectionTitle>

            <div className="grid md:grid-cols-3 gap-4">
                {items.map((p) => (
                    <div key={p.id} data-testid={`press-row-${p.id}`} className={`bg-white rounded-xl p-5 border ${p.is_active ? "border-[#E5E0D8]" : "border-red-200 opacity-70"}`}>
                        <div className="h-16 flex items-center justify-center mb-3">
                            {p.logo ? (
                                <img decoding="async" loading="lazy" src={imageUrl(p.logo)} alt={p.name} className="max-h-full max-w-full object-contain" />
                            ) : (
                                <span className="font-serif text-lg text-[#0F3A59]">{p.name}</span>
                            )}
                        </div>
                        <div className="text-xs text-[#1C1C1C]/60 truncate mb-2">{p.name}</div>
                        {p.url && <div className="text-[10px] text-[#1C1C1C]/45 truncate">{p.url}</div>}
                        <div className="flex gap-2 mt-3 items-center">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                                {p.is_active ? "Aktiv" : "Jo aktiv"}
                            </span>
                            <span className="text-[10px] text-[#1C1C1C]/55">#{p.order}</span>
                            <button onClick={() => setEditing(p)} data-testid={`press-edit-${p.id}`} className="text-xs px-3 py-1 border border-[#0F3A59]/30 rounded-full text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white ml-auto">Edit</button>
                            <button onClick={() => remove(p.id)} className="text-xs px-3 py-1 border border-red-300 rounded-full text-red-600 hover:bg-red-50">
                                <Trash2 size={12} />
                            </button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="text-center py-12 text-[#1C1C1C]/55 col-span-3 font-light">
                        Asnjë mention. Shtoni TripAdvisor, Booking, Lonely Planet etj.!
                    </div>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl w-full max-w-xl my-8 max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-[#E5E0D8] px-6 py-4 flex justify-between items-center z-10">
                            <h3 className="font-serif text-xl text-[#0F3A59]">{editing.id ? "Modifiko" : "Shto Press Mention"}</h3>
                            <button onClick={() => setEditing(null)} className="text-[#1C1C1C]/60"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <Label>Emri / Brand *</Label>
                                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="TripAdvisor" data-testid="press-name-input" />
                            </div>
                            <div>
                                <Label>Logo</Label>
                                <ImagesEditor images={editing.logo ? [editing.logo] : []} onChange={(arr) => setEditing({ ...editing, logo: arr[0] || "" })} />
                            </div>
                            <div>
                                <Label>URL (opsionale)</Label>
                                <Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://tripadvisor.com/..." />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Citim AL (opsionale)</Label>
                                    <Textarea rows={2} value={editing.quote_al} onChange={(e) => setEditing({ ...editing, quote_al: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Citim EN (opsionale)</Label>
                                    <Textarea rows={2} value={editing.quote_en} onChange={(e) => setEditing({ ...editing, quote_en: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Citim IT (opsionale)</Label>
                                    <Textarea rows={2} value={editing.quote_it} onChange={(e) => setEditing({ ...editing, quote_it: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Citim DE (opsionale)</Label>
                                    <Textarea rows={2} value={editing.quote_de} onChange={(e) => setEditing({ ...editing, quote_de: e.target.value })} />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Renditja</Label>
                                    <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
                                </div>
                                <label className="flex items-end gap-2 cursor-pointer pb-2">
                                    <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="w-4 h-4" />
                                    <span className="text-sm">Aktiv</span>
                                </label>
                            </div>
                        </div>
                        <div className="sticky bottom-0 bg-white border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-[#E5E0D8] rounded-full text-sm">Anulo</button>
                            <Button onClick={save} data-testid="press-save-btn" className="bg-[#0F3A59] text-white rounded-full">
                                <Save size={14} className="mr-2" />
                                Ruaj
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =========== WELCOME EMAIL TEMPLATE ===========
function WelcomeEmailAdmin() {
    const [tpl, setTpl] = useState(null);
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);
    const [activeLang, setActiveLang] = useState("al");
    const [preview, setPreview] = useState(null);

    const load = () => api.get("/admin/welcome-email").then((r) => setTpl(r.data));
    useEffect(() => { load(); }, []);

    if (!tpl) return <div className="text-center py-12 text-[#1C1C1C]/55">Loading...</div>;

    const set = (k, v) => setTpl({ ...tpl, [k]: v });

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/welcome-email", tpl);
            toast.success("Welcome email u ruajt");
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const runNow = async () => {
        setRunning(true);
        try {
            const res = await api.post("/admin/welcome-email/run-now");
            toast.success(`Dërguar ${res.data.sent || 0} / ${res.data.candidates || 0} kandidatë për ${res.data.target_date}`);
        } catch {
            toast.error("Run failed");
        } finally {
            setRunning(false);
        }
    };

    const showPreview = async () => {
        try {
            const res = await api.post("/admin/welcome-email/preview", { lang: activeLang });
            setPreview(res.data);
        } catch {
            toast.error("Preview failed");
        }
    };

    const LANGS = [
        { code: "al", label: "🇦🇱 AL" },
        { code: "en", label: "🇬🇧 EN" },
        { code: "it", label: "🇮🇹 IT" },
        { code: "de", label: "🇩🇪 DE" },
    ];

    return (
        <div>
            <SectionTitle
                action={
                    <div className="flex gap-2">
                        <Button onClick={showPreview} variant="outline" className="rounded-full" data-testid="welcome-preview-btn">
                            👁️ Preview
                        </Button>
                        <Button onClick={runNow} disabled={running} variant="outline" className="rounded-full" data-testid="welcome-run-btn">
                            {running ? "..." : "▶ Dërgo Tani"}
                        </Button>
                        <Button onClick={save} disabled={saving} className="bg-[#0F3A59] text-white rounded-full" data-testid="welcome-save-btn">
                            <Save size={14} className="mr-2" />
                            {saving ? "..." : "Ruaj"}
                        </Button>
                    </div>
                }
            >
                Pre-Arrival Welcome Email
            </SectionTitle>

            <div className="bg-white rounded-2xl p-6 border border-[#E5E0D8] space-y-5">
                <p className="text-sm text-[#1C1C1C]/70 font-light">
                    Email automatik që i dërgohet klientit X ditë para check-in me të gjitha info-t e rëndësishme (Wi-Fi, parking, kontakt staff, hartë). Gjuha zgjidhet automatikisht nga email (.al/.it/.de/tjetra=EN).
                </p>

                <div className="flex items-center gap-6 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={tpl.is_enabled}
                            onChange={(e) => set("is_enabled", e.target.checked)}
                            className="w-4 h-4"
                            data-testid="welcome-enabled"
                        />
                        <span className="text-sm font-medium">Aktiv (sistemi dërgon vetë)</span>
                    </label>
                    <div className="flex items-center gap-2">
                        <Label className="mb-0">Sa ditë para check-in?</Label>
                        <Input
                            type="number"
                            min="1"
                            max="14"
                            value={tpl.days_before_checkin}
                            onChange={(e) => set("days_before_checkin", parseInt(e.target.value) || 3)}
                            data-testid="welcome-days"
                            className="w-20 h-9"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#E5E0D8]">
                    <div>
                        <Label>Emri Wi-Fi</Label>
                        <Input value={tpl.wifi_name} onChange={(e) => set("wifi_name", e.target.value)} placeholder="Dea-Guest" data-testid="welcome-wifi-name" />
                    </div>
                    <div>
                        <Label>Fjalëkalimi Wi-Fi</Label>
                        <Input value={tpl.wifi_password} onChange={(e) => set("wifi_password", e.target.value)} placeholder="Welcome2026" data-testid="welcome-wifi-password" />
                    </div>
                    <div>
                        <Label>Kontakt Stafit (telefoni)</Label>
                        <Input value={tpl.staff_contact} onChange={(e) => set("staff_contact", e.target.value)} placeholder="+355 68 211 6731" />
                    </div>
                    <div>
                        <Label>URL e Hartës</Label>
                        <Input value={tpl.map_url} onChange={(e) => set("map_url", e.target.value)} placeholder="https://maps.google.com/?q=..." />
                    </div>
                </div>

                <div className="pt-2 border-t border-[#E5E0D8]">
                    <div className="flex gap-1 border-b border-[#E5E0D8] mb-3">
                        {LANGS.map((l) => (
                            <button
                                key={l.code}
                                type="button"
                                onClick={() => setActiveLang(l.code)}
                                data-testid={`welcome-lang-${l.code}`}
                                className={`px-4 py-2 text-sm border-b-2 transition-colors ${
                                    activeLang === l.code
                                        ? "border-[#0F3A59] text-[#0F3A59] font-medium"
                                        : "border-transparent text-[#1C1C1C]/55 hover:text-[#0F3A59]"
                                }`}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                    <div className="space-y-3">
                        <div>
                            <Label>Udhëzime për parking ({activeLang.toUpperCase()})</Label>
                            <Textarea
                                rows={3}
                                value={tpl[`parking_instructions_${activeLang}`] || ""}
                                onChange={(e) => set(`parking_instructions_${activeLang}`, e.target.value)}
                                placeholder={activeLang === "al" ? "Parking i lirë para hotelit. Hyrja gjendet..." : "Free parking in front of the hotel..."}
                            />
                        </div>
                        <div>
                            <Label>Mesazh personal për mysafirin ({activeLang.toUpperCase()})</Label>
                            <Textarea
                                rows={4}
                                value={tpl[`custom_message_${activeLang}`] || ""}
                                onChange={(e) => set(`custom_message_${activeLang}`, e.target.value)}
                                placeholder={activeLang === "al" ? "Po presim me kënaqësi mbërritjen tuaj..." : "We look forward to welcoming you..."}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {preview && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-[#E5E0D8] flex justify-between items-center">
                            <div>
                                <h3 className="font-serif text-lg text-[#0F3A59]">Preview ({preview.lang.toUpperCase()})</h3>
                                <p className="text-xs text-[#1C1C1C]/55 mt-0.5">Subject: {preview.subject}</p>
                            </div>
                            <button onClick={() => setPreview(null)} className="text-[#1C1C1C]/60"><X size={20} /></button>
                        </div>
                        <div className="flex-1 overflow-auto bg-[#F4EFE6]/40">
                            <iframe
                                srcDoc={preview.html}
                                title="Welcome Email Preview"
                                className="w-full h-full min-h-[500px]"
                                sandbox=""
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =========== SEARCH ANALYTICS ===========
function SearchAnalyticsAdmin() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/admin/search-analytics").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="text-center py-12 text-[#1C1C1C]/55">Loading...</div>;

    return (
        <div>
            <SectionTitle>Search Analytics</SectionTitle>

            <div className="bg-white rounded-2xl p-6 border border-[#E5E0D8] mb-6">
                <div className="font-serif text-3xl text-[#0F3A59]">{data.total_searches}</div>
                <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/55 mt-1">Kërkime totale</div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 border border-[#E5E0D8]">
                    <h3 className="font-serif text-lg text-[#0F3A59] mb-4">🔥 Termat më të kërkuara</h3>
                    {data.top_terms.length === 0 ? (
                        <p className="text-sm text-[#1C1C1C]/55 font-light">Ende pa kërkime.</p>
                    ) : (
                        <ul className="divide-y divide-[#E5E0D8]/60" data-testid="search-top-terms">
                            {data.top_terms.map((t, i) => (
                                <li key={t.term} className="py-2.5 flex items-center gap-3">
                                    <span className="font-serif text-lg text-[#CBA871] w-6">{i + 1}</span>
                                    <span className="flex-1 font-medium text-[#0F3A59]">{t.term}</span>
                                    <span className="text-xs text-[#1C1C1C]/55">{t.avg_results} rezultate mes.</span>
                                    <span className="text-sm font-medium text-[#0F3A59] w-12 text-right">×{t.count}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="bg-white rounded-2xl p-6 border border-[#E5E0D8]">
                    <h3 className="font-serif text-lg text-[#0F3A59] mb-1">💡 Pa rezultat = Ide blog!</h3>
                    <p className="text-xs text-[#1C1C1C]/55 mb-4 font-light">
                        Klientët kërkuan këto fjalë por nuk gjetën gjë. Shkruani blog posts për të mbuluar këto tema.
                    </p>
                    {data.zero_result_terms.length === 0 ? (
                        <p className="text-sm text-[#1C1C1C]/55 font-light">Asnjë kërkim pa rezultat — bravo!</p>
                    ) : (
                        <ul className="space-y-2" data-testid="search-zero-results">
                            {data.zero_result_terms.map((t) => (
                                <li key={t.term} className="flex items-center gap-3 py-1.5">
                                    <span className="flex-1 font-medium text-[#D17B6E]">"{t.term}"</span>
                                    <span className="text-xs px-2 py-0.5 bg-red-50 text-red-700 rounded-full">×{t.count}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

// =========== REVIEW REQUEST EMAIL ===========
function ReviewEmailAdmin() {
    const [tpl, setTpl] = useState(null);
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);
    const [activeLang, setActiveLang] = useState("al");
    const [preview, setPreview] = useState(null);

    const load = () => api.get("/admin/review-email").then((r) => setTpl(r.data));
    useEffect(() => { load(); }, []);

    if (!tpl) return <div className="text-center py-12 text-[#1C1C1C]/55">Loading...</div>;

    const set = (k, v) => setTpl({ ...tpl, [k]: v });

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/review-email", tpl);
            toast.success("Review email u ruajt");
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const runNow = async () => {
        setRunning(true);
        try {
            const res = await api.post("/admin/review-email/run-now");
            toast.success(`Dërguar ${res.data.sent || 0} / ${res.data.candidates || 0} kandidatë për ${res.data.target_date}`);
        } catch {
            toast.error("Run failed");
        } finally {
            setRunning(false);
        }
    };

    const showPreview = async () => {
        try {
            const res = await api.post("/admin/review-email/preview", { lang: activeLang });
            setPreview(res.data);
        } catch {
            toast.error("Preview failed");
        }
    };

    const LANGS = [
        { code: "al", label: "🇦🇱 AL" },
        { code: "en", label: "🇬🇧 EN" },
        { code: "it", label: "🇮🇹 IT" },
        { code: "de", label: "🇩🇪 DE" },
    ];

    return (
        <div>
            <SectionTitle
                action={
                    <div className="flex gap-2">
                        <Button onClick={showPreview} variant="outline" className="rounded-full" data-testid="review-preview-btn">
                            👁️ Preview
                        </Button>
                        <Button onClick={runNow} disabled={running} variant="outline" className="rounded-full" data-testid="review-run-btn">
                            {running ? "..." : "▶ Dërgo Tani"}
                        </Button>
                        <Button onClick={save} disabled={saving} className="bg-[#0F3A59] text-white rounded-full" data-testid="review-save-btn">
                            <Save size={14} className="mr-2" />
                            {saving ? "..." : "Ruaj"}
                        </Button>
                    </div>
                }
            >
                Post-Stay Review Request Email
            </SectionTitle>

            <div className="bg-white rounded-2xl p-6 border border-[#E5E0D8] space-y-5">
                <p className="text-sm text-[#1C1C1C]/70 font-light">
                    Email automatik që i dërgohet klientit X ditë pas check-out me linke direkte për të lënë review në Google / Booking.com / TripAdvisor. Sa më shumë reviews të keni, aq më shumë klientë të rinj rezervojnë!
                </p>

                <div className="flex items-center gap-6 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={tpl.is_enabled}
                            onChange={(e) => set("is_enabled", e.target.checked)}
                            className="w-4 h-4"
                            data-testid="review-enabled"
                        />
                        <span className="text-sm font-medium">Aktiv (sistemi dërgon vetë)</span>
                    </label>
                    <div className="flex items-center gap-2">
                        <Label className="mb-0">Sa ditë pas check-out?</Label>
                        <Input
                            type="number"
                            min="1"
                            max="14"
                            value={tpl.days_after_checkout}
                            onChange={(e) => set("days_after_checkout", parseInt(e.target.value) || 1)}
                            data-testid="review-days"
                            className="w-20 h-9"
                        />
                    </div>
                </div>

                <div className="pt-3 border-t border-[#E5E0D8] space-y-3">
                    <div>
                        <Label>🔗 Google Reviews URL</Label>
                        <Input
                            value={tpl.google_review_url}
                            onChange={(e) => set("google_review_url", e.target.value)}
                            placeholder="https://g.page/r/YOUR_ID/review"
                            data-testid="review-google-url"
                        />
                        <p className="text-[11px] text-[#1C1C1C]/55 mt-1">
                            Gjeje te Google Business → Get more reviews → Share review form.
                        </p>
                    </div>
                    <div>
                        <Label>🔗 Booking.com Review URL</Label>
                        <Input
                            value={tpl.booking_review_url}
                            onChange={(e) => set("booking_review_url", e.target.value)}
                            placeholder="https://www.booking.com/hotel/al/kompleksi-dea.html#tab-reviews"
                            data-testid="review-booking-url"
                        />
                    </div>
                    <div>
                        <Label>🔗 TripAdvisor Review URL</Label>
                        <Input
                            value={tpl.tripadvisor_review_url}
                            onChange={(e) => set("tripadvisor_review_url", e.target.value)}
                            placeholder="https://www.tripadvisor.com/UserReview-..."
                            data-testid="review-tripadvisor-url"
                        />
                    </div>
                </div>

                <div className="pt-2 border-t border-[#E5E0D8]">
                    <div className="flex gap-1 border-b border-[#E5E0D8] mb-3">
                        {LANGS.map((l) => (
                            <button
                                key={l.code}
                                type="button"
                                onClick={() => setActiveLang(l.code)}
                                data-testid={`review-lang-${l.code}`}
                                className={`px-4 py-2 text-sm border-b-2 transition-colors ${
                                    activeLang === l.code
                                        ? "border-[#0F3A59] text-[#0F3A59] font-medium"
                                        : "border-transparent text-[#1C1C1C]/55 hover:text-[#0F3A59]"
                                }`}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                    <div>
                        <Label>Mesazh personal ({activeLang.toUpperCase()}) — opsionale</Label>
                        <Textarea
                            rows={4}
                            value={tpl[`custom_message_${activeLang}`] || ""}
                            onChange={(e) => set(`custom_message_${activeLang}`, e.target.value)}
                            placeholder={activeLang === "al" ? "Po presim që të ndani përvojën tuaj me udhëtarë të tjerë..." : "We hope you will share your experience with other travelers..."}
                        />
                    </div>
                </div>
            </div>

            {preview && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-[#E5E0D8] flex justify-between items-center">
                            <div>
                                <h3 className="font-serif text-lg text-[#0F3A59]">Preview ({preview.lang.toUpperCase()})</h3>
                                <p className="text-xs text-[#1C1C1C]/55 mt-0.5">Subject: {preview.subject}</p>
                            </div>
                            <button onClick={() => setPreview(null)} className="text-[#1C1C1C]/60"><X size={20} /></button>
                        </div>
                        <div className="flex-1 overflow-auto bg-[#F4EFE6]/40">
                            <iframe
                                srcDoc={preview.html}
                                title="Review Email Preview"
                                className="w-full h-full min-h-[500px]"
                                sandbox=""
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =========== SITE CONTENT ===========
function SiteAdmin() {
    const [site, setSite] = useState(null);

    useEffect(() => {
        api.get("/site/content").then((r) => setSite(r.data));
    }, []);

    if (!site) return <div>Loading...</div>;

    const save = async () => {
        try {
            await api.put("/site/content", site);
            toast.success("Saved");
        } catch {
            toast.error("Save failed");
        }
    };

    const set = (k, v) => setSite({ ...site, [k]: v });

    return (
        <div>
            <SectionTitle
                action={
                    <Button onClick={save} className="bg-[#0F3A59] text-white rounded-full">
                        <Save size={16} className="mr-2" />
                        Save changes
                    </Button>
                }
            >
                Site content
            </SectionTitle>
            <div className="bg-white rounded-xl p-6 border border-[#E5E0D8] space-y-6">
                <div>
                    <Label className="block mb-2">Hero image (faqja kryesore)</Label>
                    <div className="flex items-center gap-4 flex-wrap">
                        <img decoding="async" loading="lazy"
                            src={imageUrl(site.hero_image, imageUrl("seed:hero"))}
                            alt=""
                            className="w-40 h-24 rounded-lg object-cover"
                        />
                        <ImageUploader
                            label="Replace hero"
                            onUploaded={(path) => set("hero_image", path)}
                            testid="hero-upload"
                        />
                        {site.hero_image && (
                            <button
                                onClick={() => set("hero_image", null)}
                                className="text-xs text-red-600"
                            >
                                Reset
                            </button>
                        )}
                    </div>
                </div>
                <div>
                    <Label className="block mb-2">Foto e faqes Booking (/book)</Label>
                    <div className="flex items-center gap-4 flex-wrap">
                        <img decoding="async" loading="lazy"
                            src={imageUrl(site.booking_hero_image, imageUrl("seed:gallery_pool"))}
                            alt=""
                            className="w-40 h-24 rounded-lg object-cover"
                        />
                        <ImageUploader
                            label="Replace booking image"
                            onUploaded={(path) => set("booking_hero_image", path)}
                            testid="booking-hero-upload"
                        />
                        {site.booking_hero_image && (
                            <button
                                onClick={() => set("booking_hero_image", null)}
                                className="text-xs text-red-600"
                            >
                                Reset
                            </button>
                        )}
                    </div>
                </div>
                <div>
                    <Label className="block mb-2">Foto e faqes Dhomat (/rooms)</Label>
                    <div className="flex items-center gap-4 flex-wrap">
                        <img decoding="async" loading="lazy"
                            src={imageUrl(site.rooms_hero_image, imageUrl("seed:room_gabina"))}
                            alt=""
                            className="w-40 h-24 rounded-lg object-cover"
                        />
                        <ImageUploader
                            label="Replace rooms image"
                            onUploaded={(path) => set("rooms_hero_image", path)}
                            testid="rooms-hero-upload"
                        />
                        {site.rooms_hero_image && (
                            <button
                                onClick={() => set("rooms_hero_image", null)}
                                className="text-xs text-red-600"
                            >
                                Reset
                            </button>
                        )}
                    </div>
                </div>
                <div>
                    <Label className="block mb-2">Foto e faqes Lehtësirat (/facilities)</Label>
                    <div className="flex items-center gap-4 flex-wrap">
                        <img decoding="async" loading="lazy"
                            src={imageUrl(site.facilities_hero_image, imageUrl("seed:facility_restaurant"))}
                            alt=""
                            className="w-40 h-24 rounded-lg object-cover"
                        />
                        <ImageUploader
                            label="Replace facilities image"
                            onUploaded={(path) => set("facilities_hero_image", path)}
                            testid="facilities-hero-upload"
                        />
                        {site.facilities_hero_image && (
                            <button
                                onClick={() => set("facilities_hero_image", null)}
                                className="text-xs text-red-600"
                            >
                                Reset
                            </button>
                        )}
                    </div>
                </div>
                <div>
                    <Label className="block mb-2">Foto e faqes Galeria (/gallery)</Label>
                    <div className="flex items-center gap-4 flex-wrap">
                        <img decoding="async" loading="lazy"
                            src={imageUrl(site.gallery_hero_image, imageUrl("seed:gallery_pool"))}
                            alt=""
                            className="w-40 h-24 rounded-lg object-cover"
                        />
                        <ImageUploader
                            label="Replace gallery image"
                            onUploaded={(path) => set("gallery_hero_image", path)}
                            testid="gallery-hero-upload"
                        />
                        {site.gallery_hero_image && (
                            <button
                                onClick={() => set("gallery_hero_image", null)}
                                className="text-xs text-red-600"
                            >
                                Reset
                            </button>
                        )}
                    </div>
                </div>
                <div>
                    <Label>Hero video URL (opsionale — overrides hero image)</Label>
                    <Input
                        value={site.hero_video || ""}
                        onChange={(e) => set("hero_video", e.target.value)}
                        placeholder="https://... (mp4 ose YouTube)"
                    />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Hero title (AL)</Label>
                        <Input
                            value={site.hero_title_al}
                            onChange={(e) => set("hero_title_al", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label>Hero title (EN)</Label>
                        <Input
                            value={site.hero_title_en}
                            onChange={(e) => set("hero_title_en", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label>Hero subtitle (AL)</Label>
                        <Input
                            value={site.hero_subtitle_al}
                            onChange={(e) => set("hero_subtitle_al", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label>Hero subtitle (EN)</Label>
                        <Input
                            value={site.hero_subtitle_en}
                            onChange={(e) => set("hero_subtitle_en", e.target.value)}
                        />
                    </div>
                </div>
                <div>
                    <Label>About (AL)</Label>
                    <Textarea
                        rows={3}
                        value={site.about_al}
                        onChange={(e) => set("about_al", e.target.value)}
                    />
                </div>
                <div>
                    <Label>About (EN)</Label>
                    <Textarea
                        rows={3}
                        value={site.about_en}
                        onChange={(e) => set("about_en", e.target.value)}
                    />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Phone</Label>
                        <Input value={site.phone} onChange={(e) => set("phone", e.target.value)} />
                    </div>
                    <div>
                        <Label>WhatsApp (digits only, with country code)</Label>
                        <Input
                            value={site.whatsapp}
                            onChange={(e) => set("whatsapp", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label>Email</Label>
                        <Input value={site.email} onChange={(e) => set("email", e.target.value)} />
                    </div>
                    <div>
                        <Label>Address (AL)</Label>
                        <Input
                            value={site.address_al}
                            onChange={(e) => set("address_al", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label>Address (EN)</Label>
                        <Input
                            value={site.address_en}
                            onChange={(e) => set("address_en", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label>Instagram URL</Label>
                        <Input
                            value={site.instagram}
                            onChange={(e) => set("instagram", e.target.value)}
                        />
                    </div>
                    <div>
                        <Label>Facebook URL</Label>
                        <Input
                            value={site.facebook}
                            onChange={(e) => set("facebook", e.target.value)}
                        />
                    </div>
                </div>
                <div>
                    <Label>Google Maps embed URL</Label>
                    <Textarea
                        rows={2}
                        value={site.map_embed}
                        onChange={(e) => set("map_embed", e.target.value)}
                    />
                </div>

                {/* Currency Rates (admin-editable conversion rates from EUR) */}
                <div className="pt-4 border-t border-[#E5E0D8]">
                    <Label className="block mb-2">Kurset e këmbimit (1 EUR = X)</Label>
                    <p className="text-xs text-[#1C1C1C]/55 mb-3 font-light">
                        Klientët mund të zgjedhin valutën në header. Çmimet rikalkulohen me kurset më poshtë.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {["EUR", "USD", "GBP", "ALL"].map((c) => (
                            <div key={c}>
                                <Label>1 EUR = ? {c}</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={(site.currency_rates && site.currency_rates[c]) ?? ""}
                                    onChange={(e) => set("currency_rates", {
                                        ...(site.currency_rates || {}),
                                        [c]: parseFloat(e.target.value) || 0,
                                    })}
                                    disabled={c === "EUR"}
                                    data-testid={`rate-${c}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Instagram Posts URLs */}
                <div className="pt-4 border-t border-[#E5E0D8]">
                    <Label className="block mb-2">Postimet Instagram (URL të plota)</Label>
                    <p className="text-xs text-[#1C1C1C]/55 mb-3 font-light">
                        Vendos URL të postimit Instagram (p.sh. https://www.instagram.com/p/ABC123/). Mund të shtoni 3-9 për galerinë.
                    </p>
                    <div className="space-y-2">
                        {(site.instagram_posts || []).map((url, i) => (
                            // eslint-disable-next-line react/no-array-index-key -- IG posts are edited in place; row key must be stable across keystrokes
                            <div key={`ig-${i}`} className="flex gap-2">
                                <Input
                                    value={url}
                                    onChange={(e) => {
                                        const arr = [...(site.instagram_posts || [])];
                                        arr[i] = e.target.value;
                                        set("instagram_posts", arr);
                                    }}
                                    placeholder="https://www.instagram.com/p/..."
                                    data-testid={`ig-post-${i}`}
                                />
                                <button
                                    onClick={() => {
                                        const arr = [...(site.instagram_posts || [])];
                                        arr.splice(i, 1);
                                        set("instagram_posts", arr);
                                    }}
                                    className="px-3 py-2 border border-red-300 rounded text-red-600 hover:bg-red-50"
                                    aria-label="Remove"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                        <button
                            onClick={() => set("instagram_posts", [...(site.instagram_posts || []), ""])}
                            data-testid="ig-add-btn"
                            className="text-xs px-4 py-2 border border-[#0F3A59]/30 rounded-full text-[#0F3A59] hover:bg-[#0F3A59] hover:text-white"
                        >
                            <Plus size={12} className="inline mr-1" />
                            Shto Post
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// =========== MAIN ADMIN ===========
export default function Admin() {
    const { user, logout, loading } = useAuth();
    const [tab, setTab] = useState("analytics");

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }
    if (!user) return <Navigate to="/admin/login" replace />;

    const tabs = [
        { key: "analytics", label: "Analytics", icon: BarChart3 },
        { key: "search", label: "Search", icon: BarChart3 },
        { key: "site", label: "Site", icon: Settings },
        { key: "rooms", label: "Rooms", icon: BedDouble },
        { key: "facilities", label: "Facilities", icon: UtensilsCrossed },
        { key: "addons", label: "Add-ons", icon: Sparkles },
        { key: "promo", label: "Promo", icon: Tag },
        { key: "blog", label: "Blog", icon: FileText },
        { key: "faq", label: "FAQ", icon: HelpCircle },
        { key: "press", label: "Press", icon: Star },
        { key: "welcome", label: "Welcome Email", icon: Mail },
        { key: "review", label: "Review Email", icon: Star },
        { key: "gallery", label: "Gallery", icon: ImageIcon },
        { key: "reviews", label: "Reviews", icon: Star },
        { key: "bookings", label: "Bookings", icon: CalIcon },
        { key: "messages", label: "Messages", icon: Mail },
        { key: "newsletter", label: "Newsletter", icon: Users },
    ];

    return (
        <div className="min-h-screen bg-[#F4EFE6]" data-testid="admin-dashboard">
            <header className="bg-white border-b border-[#E5E0D8] sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <Home size={18} className="text-[#0F3A59]" />
                        <span className="font-serif text-lg text-[#0F3A59]">
                            Kompleksi Dea — Admin
                        </span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <AdminCurrencySwitcher />
                        <span className="text-sm text-[#1C1C1C]/70 hidden sm:block">
                            {user.username}
                        </span>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="flex items-center gap-2 text-sm text-[#0F3A59] hover:opacity-70"
                        >
                            <LogOut size={16} />
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-8">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-white border border-[#E5E0D8] p-1 rounded-full flex flex-wrap h-auto gap-1 mb-8">
                        {tabs.map((t) => {
                            const Icon = t.icon;
                            return (
                                <TabsTrigger
                                    key={t.key}
                                    value={t.key}
                                    data-testid={`tab-${t.key}`}
                                    className="rounded-full data-[state=active]:bg-[#0F3A59] data-[state=active]:text-white px-4 py-2 text-sm"
                                >
                                    <Icon size={14} className="mr-2" />
                                    {t.label}
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                    <TabsContent value="analytics"><AnalyticsAdmin /></TabsContent>
                    <TabsContent value="search"><SearchAnalyticsAdmin /></TabsContent>
                    <TabsContent value="site"><SiteAdmin /></TabsContent>
                    <TabsContent value="rooms"><RoomsAdmin /></TabsContent>
                    <TabsContent value="facilities"><FacilitiesAdmin /></TabsContent>
                    <TabsContent value="addons"><AddonsAdmin /></TabsContent>
                    <TabsContent value="promo"><PromoAdmin /></TabsContent>
                    <TabsContent value="blog"><BlogAdmin /></TabsContent>
                    <TabsContent value="faq"><FaqAdmin /></TabsContent>
                    <TabsContent value="press"><PressAdmin /></TabsContent>
                    <TabsContent value="welcome"><WelcomeEmailAdmin /></TabsContent>
                    <TabsContent value="review"><ReviewEmailAdmin /></TabsContent>
                    <TabsContent value="gallery"><GalleryAdmin /></TabsContent>
                    <TabsContent value="reviews"><ReviewsAdmin /></TabsContent>
                    <TabsContent value="bookings"><BookingsAdmin /></TabsContent>
                    <TabsContent value="messages"><MessagesAdmin /></TabsContent>
                    <TabsContent value="newsletter"><NewsletterAdmin /></TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
